import AxiosInstance from "@/api/axios";
import { GeneralFilterType } from "@/utils/types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface LeadsStatsFilter extends GeneralFilterType {
  reengaged?: string[];
}

interface leadStats {
  tours: number;
  enquiries: number;
  trials: number;
  leads: number;
}

let abortController: AbortController | null = null;
let salesAbortController: AbortController | null = null;

export const fetchLeadsStats = createAsyncThunk(
  "fetchLeadsStats/fetchData",
  async (
    {
      organization_id,
      agent,
      branch_id,
      date_range,
      region_id,
      reengaged,
    }: LeadsStatsFilter,
    thunkAPI,
  ) => {
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();
    try {
      const response = await AxiosInstance.get(`/leads/stats`, {
        params: {
          organization_id,
          branch_id,
          date_range,
          agent,
          region_id,
          reengaged,
        },
        signal: abortController.signal,
      });
      if (response.data.success) {
        return response.data.data;
      } else {
        return null;
      }
    } catch (error: any) {
      if (!abortController.signal.aborted || error.code === "ERR_CANCELED") {
        return null;
      }
      return thunkAPI.rejectWithValue(error);
    } finally {
      abortController = null;
    }
  },
);

export const fetchSalesStats = createAsyncThunk(
  "fetchLeadsStats/fetchSales",
  async (
    {
      organization_id,
      agent,
      branch_id,
      date_range,
      region_id,
      reengaged,
    }: LeadsStatsFilter,
    thunkAPI,
  ) => {
    if (salesAbortController) {
      salesAbortController.abort();
    }

    salesAbortController = new AbortController();
    try {
      const response = await AxiosInstance.get(`/leads/stats/sales`, {
        params: {
          organization_id,
          branch_id,
          date_range,
          agent,
          region_id,
          reengaged,
        },
        signal: salesAbortController.signal,
      });
      if (response.data.success) {
        return response.data.data;
      } else {
        return null;
      }
    } catch (error: any) {
      if (!salesAbortController.signal.aborted || error.code === "ERR_CANCELED") {
        return null;
      }
      return thunkAPI.rejectWithValue(error);
    } finally {
      salesAbortController = null;
    }
  },
);

interface LeadsStateType {
  data: leadStats | null;
  loading: boolean;
  sales: number | null;
  salesLoading: boolean;
  error: string | null;
}

const initialState: LeadsStateType = {
  data: null,
  loading: false,
  sales: null,
  salesLoading: false,
  error: null,
};

const leadsReducer = createSlice({
  name: "leadsStats",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeadsStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeadsStats.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchLeadsStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSalesStats.pending, (state) => {
        state.salesLoading = true;
      })
      .addCase(fetchSalesStats.fulfilled, (state, action) => {
        state.salesLoading = false;
        state.sales = action.payload?.sales ?? null;
      })
      .addCase(fetchSalesStats.rejected, (state) => {
        state.salesLoading = false;
      });
  },
});

export default leadsReducer.reducer;
